import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  facebookLoginRequest,
  forgotPasswordRequest,
  googleLoginRequest,
  loginRequest,
  logoutRequest,
  meRequest,
  registerRequest,
  resendVerificationEmailRequest,
  updateEmailRequest,
  updatePasswordRequest,
  updateProfileRequest,
  resetPasswordRequest,
} from "../../api/authApi";
import { UpdateProfileRequest } from "../../models/RequestModels/UpdateProfileRequest";
import { UpdateEmailRequest } from "../../models/RequestModels/UpdateEmailRequest";
import { UpdatePasswordRequest } from "../../models/RequestModels/UpdatePasswordRequest";
import { LoginRequest } from "../../models/RequestModels/LoginRequest";
import { RegisterRequest } from "../../models/RequestModels/RegisterRequest";
import { ForgotPasswordRequest } from "../../models/RequestModels/ForgotPasswordRequest";
import { SocialLoginRequest } from "../../models/RequestModels/SocialLoginRequest";
import { FacebookLoginRequest } from "../../models/RequestModels/FacebookLoginRequest";
import { AccountResponse } from "../../models/ResponseModels/AccountResponse";
import { AuthUserResponse } from "../../models/ResponseModels/AuthUserResponse";
import {
  updateAccountSettingsRequest,
  cancelAccountDeletionRequest,
  requestAccountDeletionRequest,
} from "../../api/accountApi";
import { ResetPasswordRequest } from "../../models/RequestModels/ResetPasswordRequest";
import { UpdateAccountSettingsRequest } from "../../models/RequestModels/UpdateAccountSettingsRequest";
import {
  getToken,
  removeToken,
  saveToken,
} from "../../utils/helperFunctions/tokenStorage";

type FieldErrors = Record<string, string[]>;

interface AuthRejectPayload {
  message: string;
  fieldErrors?: FieldErrors;
}

interface AuthState {
  user: AuthUserResponse | null;
  account: AccountResponse | null;
  token: string | null;
  loading: boolean;
  socialLoading: boolean;
  initialized: boolean;
  error: string | null;
  fieldErrors: FieldErrors;
}

const initialState: AuthState = {
  user: null,
  account: null,
  token: null,
  loading: false,
  socialLoading: false,
  initialized: false,
  error: null,
  fieldErrors: {},
};

const getAuthErrorPayload = (error: any): AuthRejectPayload => {
  const data = error?.response?.data;

  if (data?.errors) {
    return {
      message:
        data?.message ||
        data?.errors?.message?.[0] ||
        data?.errors?.email?.[0] ||
        "Request failed.",
      fieldErrors: data.errors,
    };
  }

  return {
    message: data?.message || error?.message || "Request failed.",
  };
};

const saveAuthResponseToken = async <T extends { token: string }>(
  response: T,
): Promise<T> => {
  await saveToken(response.token);
  return response;
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (body: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await loginRequest(body);
      return await saveAuthResponseToken(response);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (body: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await registerRequest(body);
      return await saveAuthResponseToken(response);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (body: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordRequest(body);
      return response.message;
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (body: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await resetPasswordRequest(body);
      return response.message;
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const resendVerificationEmail = createAsyncThunk(
  "auth/resendVerificationEmail",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await resendVerificationEmailRequest(email);
      return response.message;
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (body: SocialLoginRequest, { rejectWithValue }) => {
    try {
      const response = await googleLoginRequest(body);
      return await saveAuthResponseToken(response);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const loginWithFacebook = createAsyncThunk(
  "auth/loginWithFacebook",
  async (body: FacebookLoginRequest, { rejectWithValue }) => {
    try {
      const response = await facebookLoginRequest(body);
      return await saveAuthResponseToken(response);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const loadCurrentUser = createAsyncThunk(
  "auth/loadCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = await getToken();

      if (!token) {
        return null;
      }

      const response = await meRequest();

      return {
        ...response,
        token,
      };
    } catch (error: any) {
      await removeToken();
      return rejectWithValue({
        message: error?.response?.data?.message || "Session expired.",
      });
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await logoutRequest();
      await removeToken();
      return true;
    } catch (error: any) {
      await removeToken();
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (body: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      return await updateProfileRequest(body);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const updateEmail = createAsyncThunk(
  "auth/updateEmail",
  async (body: UpdateEmailRequest, { rejectWithValue }) => {
    try {
      return await updateEmailRequest(body);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const updatePassword = createAsyncThunk(
  "auth/updatePassword",
  async (body: UpdatePasswordRequest, { rejectWithValue }) => {
    try {
      return await updatePasswordRequest(body);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const updateAccountSettings = createAsyncThunk(
  "auth/updateAccountSettings",
  async (body: UpdateAccountSettingsRequest, { rejectWithValue }) => {
    try {
      return await updateAccountSettingsRequest(body);
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const requestAccountDeletion = createAsyncThunk(
  "auth/requestAccountDeletion",
  async (_, { rejectWithValue }) => {
    try {
      return await requestAccountDeletionRequest();
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

export const cancelAccountDeletion = createAsyncThunk(
  "auth/cancelAccountDeletion",
  async (_, { rejectWithValue }) => {
    try {
      return await cancelAccountDeletionRequest();
    } catch (error: any) {
      return rejectWithValue(getAuthErrorPayload(error));
    }
  },
);

const applyAuthSuccess = (
  state: AuthState,
  action: PayloadAction<{
    user: AuthUserResponse;
    account: AccountResponse;
    token: string;
  }>,
) => {
  state.loading = false;
  state.socialLoading = false;
  state.error = null;
  state.fieldErrors = {};
  state.user = action.payload.user;
  state.account = action.payload.account;
  state.token = action.payload.token;
};

const applyAuthReject = (state: AuthState, action: any) => {
  const payload = action.payload as AuthRejectPayload | undefined;

  state.loading = false;
  state.socialLoading = false;
  state.error = payload?.message || "Request failed.";
  state.fieldErrors = payload?.fieldErrors || {};
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    clearAuthFieldError: (state, action: PayloadAction<string>) => {
      delete state.fieldErrors[action.payload];
      state.error = null;
    },
    markSubscriptionExpired: (state) => {
      if (!state.account) {
        return;
      }

      state.account.has_active_access = false;
      state.account.remaining_days = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(loginUser.fulfilled, applyAuthSuccess)
      .addCase(loginUser.rejected, applyAuthReject)

      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(registerUser.fulfilled, applyAuthSuccess)
      .addCase(registerUser.rejected, applyAuthReject)

      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, applyAuthReject)
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, applyAuthReject)
      .addCase(resendVerificationEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendVerificationEmail.rejected, applyAuthReject)

      .addCase(loginWithGoogle.pending, (state) => {
        state.socialLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, applyAuthSuccess)
      .addCase(loginWithGoogle.rejected, applyAuthReject)

      .addCase(loginWithFacebook.pending, (state) => {
        state.socialLoading = true;
        state.error = null;
      })
      .addCase(loginWithFacebook.fulfilled, applyAuthSuccess)
      .addCase(loginWithFacebook.rejected, applyAuthReject)

      .addCase(loadCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;

        if (action.payload) {
          state.user = action.payload.user;
          state.account = action.payload.account;
          state.token = action.payload.token;
        }
      })
      .addCase(loadCurrentUser.rejected, (state, action: any) => {
        state.loading = false;
        state.initialized = true;
        state.user = null;
        state.account = null;
        state.token = null;
        state.error = action.payload?.message || "Session expired.";
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.account = null;
        state.token = null;
        state.loading = false;
        state.socialLoading = false;
        state.error = null;
        state.fieldErrors = {};
      })

      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.account = action.payload.account;
      })
      .addCase(updateProfile.rejected, applyAuthReject)

      .addCase(updateEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.account = action.payload.account;
      })
      .addCase(updateEmail.rejected, applyAuthReject)

      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePassword.rejected, applyAuthReject)
      .addCase(updateAccountSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAccountSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.account = action.payload.account;
      })
      .addCase(updateAccountSettings.rejected, applyAuthReject)

      .addCase(requestAccountDeletion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestAccountDeletion.fulfilled, (state, action) => {
        state.loading = false;
        state.account = action.payload.account;
      })
      .addCase(requestAccountDeletion.rejected, applyAuthReject)

      .addCase(cancelAccountDeletion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelAccountDeletion.fulfilled, (state, action) => {
        state.loading = false;
        state.account = action.payload.account;
      })
      .addCase(cancelAccountDeletion.rejected, applyAuthReject);
  },
});

export const { clearAuthError, clearAuthFieldError, markSubscriptionExpired } =
  authSlice.actions;

export default authSlice.reducer;
